import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
} from "react";
import { TOOLTIP_STORAGE_KEY } from "./tooltip-registry";
import { evaluateNextTooltip, ProjectState } from "./tooltip-rules";

interface TooltipStorageState {
  dismissCounts: Record<string, number>;
  version: number;
}

interface TooltipContextValue {
  activeTooltipId: string | null;
  dismissCounts: Record<string, number>;
  registerTarget: (tooltipId: string, element: HTMLElement | null) => void;
  unregisterTarget: (tooltipId: string) => void;
  getTargetElement: (tooltipId: string) => HTMLElement | null;
  dismissTooltip: (tooltipId: string) => void;
  setProjectState: (state: ProjectState) => void;
  setCurrentToolbox: (toolbox: string | undefined) => void;
  setOnDismissNavigate: (callback: ((tooltipId: string) => void) | undefined) => void;
}

// Helper to create a stable key from project state for comparison
function projectStateKey(state: ProjectState): string {
  return `${state.hasGPSTrack}-${state.hasEditedStaticLabels}-${state.hasMapLabels}`;
}

const TooltipContext = createContext<TooltipContextValue | null>(null);

const STORAGE_VERSION = 1;

function loadStorageState(): TooltipStorageState {
  try {
    const stored = localStorage.getItem(TOOLTIP_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored) as TooltipStorageState;
      if (parsed.version === STORAGE_VERSION) {
        return parsed;
      }
    }
  } catch (e) {
    console.warn("Failed to load tooltip state from localStorage:", e);
  }
  return { dismissCounts: {}, version: STORAGE_VERSION };
}

function saveStorageState(state: TooltipStorageState): void {
  try {
    localStorage.setItem(TOOLTIP_STORAGE_KEY, JSON.stringify(state));
  } catch (e) {
    console.warn("Failed to save tooltip state to localStorage:", e);
  }
}

interface TooltipProviderProps {
  children: ReactNode;
}

export function TooltipProvider({ children }: TooltipProviderProps) {
  const [dismissCounts, setDismissCounts] = useState<Record<string, number>>(
    () => loadStorageState().dismissCounts
  );
  const [activeTooltipId, setActiveTooltipId] = useState<string | null>(null);
  const [projectState, setProjectState] = useState<ProjectState>({
    hasGPSTrack: false,
    hasEditedStaticLabels: false,
    hasMapLabels: false,
  });
  const [currentToolbox, setCurrentToolbox] = useState<string | undefined>(
    undefined
  );

  // Track which tooltips have been shown for the current project state
  // Key is the project state key, value is set of tooltip IDs shown for that state
  const shownForStateRef = useRef<Map<string, Set<string>>>(new Map());

  // Callback to navigate to appropriate toolbox when tooltip is dismissed
  const onDismissNavigateRef = useRef<((tooltipId: string) => void) | undefined>(undefined);

  const setOnDismissNavigate = useCallback((callback: ((tooltipId: string) => void) | undefined) => {
    onDismissNavigateRef.current = callback;
  }, []);

  const targetRefs = useRef<Map<string, HTMLElement | null>>(new Map());

  // Register a target element for a tooltip
  const registerTarget = useCallback(
    (tooltipId: string, element: HTMLElement | null) => {
      targetRefs.current.set(tooltipId, element);
    },
    []
  );

  // Unregister a target element
  const unregisterTarget = useCallback((tooltipId: string) => {
    targetRefs.current.delete(tooltipId);
  }, []);

  // Get the target element for a tooltip
  const getTargetElement = useCallback((tooltipId: string) => {
    return targetRefs.current.get(tooltipId) || null;
  }, []);

  // Dismiss a tooltip and increment its count
  const dismissTooltip = useCallback((tooltipId: string) => {
    // Mark this tooltip as shown for the current project state
    const stateKey = projectStateKey(projectState);
    const shownSet = shownForStateRef.current.get(stateKey) || new Set();
    shownSet.add(tooltipId);
    shownForStateRef.current.set(stateKey, shownSet);

    setDismissCounts((prev) => {
      const newCounts = {
        ...prev,
        [tooltipId]: (prev[tooltipId] || 0) + 1,
      };
      saveStorageState({ dismissCounts: newCounts, version: STORAGE_VERSION });
      return newCounts;
    });
    setActiveTooltipId(null);

    // Navigate to the appropriate toolbox if callback is set
    onDismissNavigateRef.current?.(tooltipId);
  }, [projectState]);

  // Re-evaluate which tooltip to show when state changes
  useEffect(() => {
    const stateKey = projectStateKey(projectState);
    const shownForCurrentState = shownForStateRef.current.get(stateKey);

    const nextTooltip = evaluateNextTooltip(
      projectState,
      dismissCounts,
      currentToolbox
    );

    // Only show the tooltip if it hasn't been shown for this project state yet
    if (nextTooltip && shownForCurrentState?.has(nextTooltip)) {
      setActiveTooltipId(null);
    } else {
      setActiveTooltipId(nextTooltip);
    }
  }, [projectState, dismissCounts, currentToolbox]);

  const value: TooltipContextValue = {
    activeTooltipId,
    dismissCounts,
    registerTarget,
    unregisterTarget,
    getTargetElement,
    dismissTooltip,
    setProjectState,
    setCurrentToolbox,
    setOnDismissNavigate,
  };

  return (
    <TooltipContext.Provider value={value}>{children}</TooltipContext.Provider>
  );
}

export function useTooltipContext(): TooltipContextValue {
  const context = useContext(TooltipContext);
  if (!context) {
    throw new Error("useTooltipContext must be used within a TooltipProvider");
  }
  return context;
}
